import React, { useState, useEffect } from 'react';
import { auth } from './firebase';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  onAuthStateChanged
} from 'firebase/auth';
import './Login.css';

function Login() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [leagueName, setLeagueName] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false
  });
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  // Check if user is already authenticated (from Flutter)
  useEffect(() => {
    console.log('Login component mounted, checking auth state...');
    console.log('Is native app?', window.isNativeApp);
    console.log('Flutter token?', window.flutterFirebaseToken ? 'YES' : 'NO');
    
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log('User already authenticated:', user.email);
        // User is already signed in (from Flutter or previous web session)
        // The App.js will handle navigation to Dashboard
      } else {
        console.log('No authenticated user');
      }
    });

    return () => unsubscribe();
  }, []);

  const validatePassword = (pwd) => {
    const strength = {
      minLength: pwd.length >= 8,
      hasUpperCase: /[A-Z]/.test(pwd),
      hasLowerCase: /[a-z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(pwd)
    };
    setPasswordStrength(strength);
    return Object.values(strength).every(req => req === true);
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    validatePassword(newPassword);
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (isSignUp) {
      // Validate access code with backend
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${apiUrl}/api/validate-access-code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ accessCode })
        });
        
        const data = await response.json();
        
        if (!data.valid) {
          setError('Invalid access code. Contact support@warriorwalkupmusic.com to get access.');
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('Error validating access code:', error);
        setError('Unable to validate access code. Please try again.');
        setLoading(false);
        return;
      }

      // Validate password strength
      if (!validatePassword(password)) {
        setError('Please meet all password requirements');
        setLoading(false);
        return;
      }
    }

    try {
      if (isSignUp) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        // Save additional profile data to Firestore
        const { db } = await import('./firebase');
        const { doc, setDoc } = await import('firebase/firestore');
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          name: '',
          phone: phone,
          leagueName: leagueName,
          city: city,
          state: state,
          email: email,
          createdAt: new Date()
        });
        console.log('User created:', userCredential.user);
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        console.log('User signed in:', userCredential.user);
      }
    } catch (err) {
      setError(err.message);
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    const provider = new GoogleAuthProvider();

    try {
      const result = await signInWithPopup(auth, provider);
      console.log('Google sign in successful:', result.user);
    } catch (err) {
      setError(err.message);
      console.error('Google sign in error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      alert('Password reset email sent! Check your inbox or spam folder.');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (err) {
      setError(err.message);
      console.error('Password reset error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>Warrior Walkup Music</h1>
        <p className="subtitle">AI Powered Beats for Warrior Players</p>

        <div className="auth-tabs">
          <button 
            className={!isSignUp ? 'active' : ''} 
            onClick={() => setIsSignUp(false)}
          >
            Sign In
          </button>
          <button 
            className={isSignUp ? 'active' : ''} 
            onClick={() => setIsSignUp(true)}
          >
            Sign Up
          </button>
        </div>

        {error && !isSignUp && <div className="error-message">{error}</div>}

        <form onSubmit={handleEmailAuth}>
          
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={handlePasswordChange}
            onFocus={() => setShowPasswordRequirements(true)}
            required
          />
          {isSignUp && showPasswordRequirements && (
            <div className="password-requirements">
              <p className="requirements-title">Password must contain:</p>
              <ul>
                <li className={passwordStrength.minLength ? 'met' : 'unmet'}>
                  {passwordStrength.minLength ? '✓' : '○'} At least 8 characters
                </li>
                <li className={passwordStrength.hasUpperCase ? 'met' : 'unmet'}>
                  {passwordStrength.hasUpperCase ? '✓' : '○'} One uppercase letter
                </li>
                <li className={passwordStrength.hasLowerCase ? 'met' : 'unmet'}>
                  {passwordStrength.hasLowerCase ? '✓' : '○'} One lowercase letter
                </li>
                <li className={passwordStrength.hasNumber ? 'met' : 'unmet'}>
                  {passwordStrength.hasNumber ? '✓' : '○'} One number
                </li>
                <li className={passwordStrength.hasSpecialChar ? 'met' : 'unmet'}>
                  {passwordStrength.hasSpecialChar ? '✓' : '○'} One special character (!@#$%^&*)
                </li>
              </ul>
            </div>
          )}

          {isSignUp && error && <div className="error-message">{error}</div>}

          {isSignUp && (
            <>
              <input
                type="tel"
                placeholder="Phone Number (optional)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <input
                type="text"
                placeholder="League/School Name *"
                value={leagueName}
                onChange={(e) => setLeagueName(e.target.value)}
                required
              />
              <input
                type="text"
                placeholder="City *"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
              />
              <input
                type="text"
                placeholder="State *"
                value={state}
                onChange={(e) => setState(e.target.value)}
                required
              />
              <input
                type="text"
                placeholder="Access Code"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                required
                className="access-code-input"
              />
            </>
          )}

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'Loading...' : (isSignUp ? 'Create Account' : 'Sign In')}
          </button>

          {!isSignUp && (
            <button 
              type="button" 
              className="forgot-password-link"
              onClick={() => setShowForgotPassword(true)}
            >
              Forgot Password?
            </button>
          )}
        </form>

        <div className="divider">
          <span>OR</span>
        </div>

        <button className="google-btn" onClick={handleGoogleSignIn} disabled={loading}>
          <span className="google-icon">G</span>
          Continue with Google
        </button>
        
        {isSignUp && (
          <div className="access-info">
            <p>Don't have an access code?</p>
            <p>Interested in using Warrior Walkup Music for your team or league?</p>
            <p>Contact <a href="mailto:support@warriorwalkupmusic.com">support@warriorwalkupmusic.com</a></p>
          </div>
        )}
      </div>

      <div className="support-footer">
        <p>Contact Us: <a href="mailto:support@warriorwalkupmusic.com">support@warriorwalkupmusic.com</a></p>
      </div>

      <footer style={{
        textAlign: 'center',
        padding: '12px 20px',
        marginTop: '8px',
        borderTop: '1px solid #333',
        fontSize: '0.85rem',
        color: '#888'
      }}>
        <a href="https://warriorwalkupmusic.com/privacy" target="_blank" rel="noreferrer" style={{ color: '#888', marginRight: '20px', textDecoration: 'underline' }}>Privacy Policy</a>
        <a href="https://warriorwalkupmusic.com/terms" target="_blank" rel="noreferrer" style={{ color: '#888', textDecoration: 'underline' }}>Terms of Service</a>
        <p style={{ marginTop: '8px', fontSize: '0.8rem' }}>© 2026 Nerdette Ventures LLC. All rights reserved.</p>
      </footer>

      {showForgotPassword && (
        <div className="modal-overlay" onClick={() => setShowForgotPassword(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Reset Password</h2>
            <p>Enter your email address and we'll send you a reset link.</p>
            
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handlePasswordReset}>
              <input
                type="email"
                placeholder="Email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
              />
              <div className="modal-buttons">
                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => setShowForgotPassword(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;